'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Header from '../../components/Header';
import Footer from '@/components/Footer';
import ScrollTopButton from '@/components/ScrollTopButton';

const CATEGORIES = ['전체', '공고'];
const ITEMS_PER_PAGE = 10;

export default function BoardListClient({ posts }) {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const router = useRouter();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const filtered = posts.filter((post) => {
    const matchCategory =
      selectedCategory === '전체' || post.category === selectedCategory;
    const matchSearch =
      searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.contents.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.isNotice && !b.isNotice) return -1;
    if (!a.isNotice && b.isNotice) return 1;
    return new Date(b.date.replace(/\./g, '-')) - new Date(a.date.replace(/\./g, '-'));
  });

  const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
  const paginated = sorted.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };

  return (
    <div className="font-sans min-h-screen">
      <Header />
      <ScrollTopButton />

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className="px-4 sm:px-6 lg:px-44">
          <div
            className={`flex items-center gap-4 mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}
          >
            <div className="w-20 h-1 bg-blue-600"></div>
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              Investor Relations
            </span>
          </div>
          <h2
            className={`text-5xl md:text-7xl font-black text-gray-800 mb-6 transition-all duration-700 delay-150 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}
          >
            투자정보
          </h2>
          <p
            className={`text-base md:text-xl text-gray-600 max-w-3xl leading-relaxed transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}
          >
            KD다츠의 투자정보 및 공고를 확인하세요.
            <br />
            결산공고 등 주요 공시 내용을 안내드립니다.
          </p>
        </div>
      </section>

      {/* Board Section */}
      <main className="flex flex-col text-gray-800">
        <section className="pt-6 pb-20 bg-gray-100">
          <section className="px-4 pb-20 mx-auto pt-14 sm:px-6 lg:pt-10 lg:px-44">

            {/* 카테고리 탭 + 검색 */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className={`px-6 py-2 text-sm font-bold transition-all duration-200 border-b-2 ${
                      selectedCategory === cat
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-200 hover:text-gray-800 hover:border-gray-400'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSearch} className="flex gap-0">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="검색어를 입력하세요"
                  className="bg-white text-gray-800 placeholder-gray-400 border border-gray-300 border-r-0 px-5 py-3 text-sm focus:outline-none focus:bg-gray-50 w-52 md:w-64"
                />
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-sm font-bold transition-colors duration-200 uppercase tracking-wider"
                >
                  Search
                </button>
              </form>
            </div>

            {/* 테이블 */}
            <div className="border-t-4 border-blue-600 bg-white">
              {/* 헤더 */}
              <div className="hidden md:grid grid-cols-[80px_100px_1fr_100px_110px] gap-4 px-6 py-4 bg-gray-100 text-gray-500 text-xs font-bold uppercase tracking-widest border-b border-gray-200">
                <span className="text-center">No.</span>
                <span className="text-center">Category</span>
                <span className="text-left">Subject</span>
                <span className="text-center">Author</span>
                <span className="text-center">Date</span>
              </div>

              {paginated.length === 0 ? (
                <div className="py-24 text-center text-gray-400 text-base">
                  표시할 게시글이 없습니다.
                </div>
              ) : (
                paginated.map((post) => (
                  <div
                    key={post.id}
                    onClick={() => router.push(`/board/${post.id}`)}
                    className={`group flex flex-col md:grid md:grid-cols-[80px_100px_1fr_100px_110px] gap-2 md:gap-4 px-6 py-5 border-b border-gray-200 cursor-pointer transition-all duration-300 hover:bg-blue-600/90 ${
                      post.isNotice ? 'bg-blue-50' : 'bg-white'
                    }`}
                  >
                    {/* 번호 */}
                    <span className="hidden md:flex items-center justify-center text-sm text-gray-400 group-hover:text-white font-medium">
                      {post.isNotice
                        ? <span className="text-blue-600 font-bold group-hover:text-white text-xs px-2 py-0.5 border border-blue-600 group-hover:border-white">NOTICE</span>
                        : sorted.length - sorted.indexOf(post)
                      }
                    </span>

                    {/* 분류 */}
                    <span className="hidden md:flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600 group-hover:text-white transition-colors duration-300">
                        {post.category}
                      </span>
                    </span>

                    {/* 제목 */}
                    <div className="flex items-center gap-3">
                      <span className="md:hidden text-xs font-bold text-blue-600 group-hover:text-white">
                        {post.category}
                      </span>
                      <span
                        className={`text-base font-semibold truncate transition-colors duration-300 group-hover:text-white ${
                          post.isNotice ? 'text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {post.title}
                      </span>
                      {post.isNotice && (
                        <span className="hidden md:inline-flex items-center text-[10px] bg-blue-600 text-white px-2 py-0.5 font-black group-hover:bg-white group-hover:text-blue-600 transition-colors duration-300">
                          NEW
                        </span>
                      )}
                    </div>

                    {/* 작성자 */}
                    <span className="hidden md:flex items-center justify-center text-sm text-gray-500 group-hover:text-blue-100">
                      {post.author}
                    </span>

                    {/* 날짜 */}
                    <span className="hidden md:flex items-center justify-center text-sm text-gray-500 group-hover:text-blue-100">
                      {post.date}
                    </span>

                    {/* 모바일 메타 */}
                    <div className="md:hidden flex items-center gap-4 text-xs text-gray-400 group-hover:text-blue-100">
                      <span className="font-bold">{post.author}</span>
                      <span>{post.date}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-12">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 text-gray-500 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 text-sm font-bold transition-all ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-500 border border-gray-300 hover:bg-gray-100 hover:text-gray-800'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 text-gray-500 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
